import { getRecoil, getRecoilPromise } from '../../../state/recoilNexus'
import { cmsObjectsLoader, productDataSelector } from '../../../state/state'
import { Types } from '../../../cms/types'
import { Numbers } from '../../../utils/numbers'
import { Lists } from '../../../utils/lists'
import { Strings } from '../../../utils/strings'
import { Assessment } from '../../../cms/models/Assessment'
import { QualificationSize } from '../../../cms/models/QualificationSize'
import { ContentType } from '../../../cms/models/ContentType'
import { ProductDataTree } from '../../../cms/models/__ProductDataTree'
import { BulkFile } from '../../../cms/models/BulkFile'
import { INTERNATIONAL_STREAM, OCR_STREAM } from '../../../utils/constants'
import moment from 'moment'
import { fi } from '../../../utils/helpers'

const specialContentTypes = ['sa', 'ec']

const enum titleRules {
	none = 'none',
	special = 'special',
	examination = 'examination',
	nonAssessment = 'non-assessment'
}

/**
 * Parses a filename following the International stream naming convention
 * into a BulkFile with pre-populated data fields.
 */
export function parseFileName(fileName: string): BulkFile {
	const data: any = { filename: fileName }
	const parts = fileName.split('.')[0].split('_')
	let title: string[] = []
	const contentTypes = getRecoil(cmsObjectsLoader([Types.CONTENT_TYPE]))
	const productData = getRecoil(productDataSelector)

	let rule: string = titleRules.none
	let years: string = ''

	parts.forEach((part, idx) => {
		if (idx === 0) {
			const [assessment, subject, qualification] =
				productData.getAssessment(part)
			if (assessment) {
				const a: Assessment = assessment as Assessment
				data.assessment = [a.getId()]
				title.push(a.code)
				title.push(a.name)
				data.child_assessment = a.childAssessments.map((c) => c.getId())
				data.component = a.components.map((c) => c.getId())
				if (a.specification_group) {
					data.specification_group = [...a.specification_group]
				}
				if (subject) {
					data.subject = [subject.getId()]
				}
				if (qualification) {
					data.qualification = [qualification.getId()]
					data.qualification_group = [...qualification.qualification_group]
				}
			}
			return
		}

		if ('ymsw'.includes(part[0]) && Numbers.default(part[1])) {
			const year = part.substring(1).split('-')

			if (year.length === 1) {
				data.exam_year = [2000 + +year[0]]
				years = data.exam_year[0].toString()
			} else if (year.length === 2) {
				data.exam_year = []
				for (let i = +year[0]; i <= +year[1]; i++) {
					data.exam_year.push(2000 + i)
				}
				years =
					data.exam_year[0] + '-' + data.exam_year[data.exam_year.length - 1]
			}

			switch (part[0]) {
				case 'm':
					data.series = 'MARCH'
					title.push('March')
					break
				case 'w':
					data.series = 'NOVEMBER'
					title.push('November')
					break
				case 's':
					data.series = 'JUNE'
					title.push('June')
					break
			}

			title.push(years)
			return
		}

		const ct = contentTypes.find((ct: any) => ct.keyword === part)

		if (ct) {
			if (specialContentTypes.includes(part)) {
				rule = titleRules.special
			}
			if (ct.hasTag('#non-assessment')) {
				rule = titleRules.nonAssessment
			}
			if (rule === titleRules.special || rule === titleRules.nonAssessment) {
				title = [title[0]]
			}
			data.content_type = ct.getId()
			title.push((ct as ContentType).name)
		}
	})

	const paperNumber = parts[parts.length - 1]

	if (+paperNumber) {
		switch (rule) {
			case titleRules.special:
				if (years.includes('-')) {
					const rangeParts = years.split('-')
					title.push(
						` Paper ${+paperNumber} (for examination from ${rangeParts[0]} to ${rangeParts[1]})`
					)
				} else {
					title.push(` Paper ${+paperNumber} (for examination from ${years})`)
				}
				break
			case titleRules.nonAssessment:
				if (years.includes('-')) {
					const rangeParts = years.split('-')
					title.push(` (for examination from ${rangeParts[0]} to ${rangeParts[1]})`)
				} else {
					title.push(` (for examination from ${years})`)
				}
				break
			default:
				title.push(` ${paperNumber}`)
		}
		data.paper_number = paperNumber
	}

	data.title = title.join(' ')

	return new BulkFile({ filename: fileName, data })
}

/**
 * Parses an uploaded Excel template into BulkFile instances.
 */
export async function parseTemplate(
	rows: string[][][],
	businessStream: string
): Promise<BulkFile[]> {
	const response: BulkFile[] = []
	const productData = await getRecoilPromise(productDataSelector)
	const contentTypes: ContentType[] = (await getRecoilPromise(
		cmsObjectsLoader([Types.CONTENT_TYPE])
	)) as any[]

	if (rows.length !== 2) {
		return []
	}

	rows[0].forEach((row) => {
		if (Lists.default(row).length) {
			response.push(
				parseExcelRow(row, false, contentTypes, productData, businessStream)
			)
		}
	})

	rows[1].forEach((row) => {
		if (Lists.default(row).length) {
			response.push(
				parseExcelRow(row, true, contentTypes, productData, businessStream)
			)
		}
	})

	return response
}

function parseExcelRow(
	row: string[],
	isTechnicals: boolean,
	contentTypes: ContentType[],
	productData: ProductDataTree,
	businessStream: string
): BulkFile {
	let [
		title,
		tag,
		filename,
		description,
		assessment,
		content_type,
		unit,
		...rest
	] = row

	let exam_year: any, series: any, public_from: any, component: any, paper_number: any

	switch (businessStream) {
		case OCR_STREAM:
			if (isTechnicals) {
				;[exam_year, series, public_from] = rest
			} else {
				;[component, exam_year, series, public_from] = rest
			}
			break
		case INTERNATIONAL_STREAM:
			;({ paper_number, exam_year, series } =
				parseFileName(filename))
			;[component, public_from] = rest
			break
	}

	const data: any = {
		title,
		description
	}

	const isValidYear = (year: any) => {
		const currentYear = new Date().getFullYear()
		return +year > 2004 && +year < currentYear + 40 && Number.isInteger(+year)
	}

	if (exam_year && businessStream === INTERNATIONAL_STREAM) {
		const years = exam_year
			.split(',')
			.flatMap((year: any) => {
				if (isValidYear(year)) {
					return +year
				}

				if (year.includes('-')) {
					const range: number[] = []
					const yearParts = year.split('-')

					const start = yearParts[0]
					const end = yearParts[yearParts.length - 1]

					if (
						+start &&
						+end &&
						+start <= +end &&
						isValidYear(start) &&
						isValidYear(end)
					) {
						for (let i = +start; i <= +end; i++) {
							range.push(i)
						}
						return range
					}
					return `${year} <INVALID YEAR>`
				}

				return `${year} <INVALID YEAR>`
			})
			.sort()

		const yearRanges = Array.from(new Set(years))

		if (yearRanges.length !== 0) {
			data.exam_year = yearRanges
		}
	}

	if (exam_year && businessStream === OCR_STREAM) {
		data.exam_year = exam_year
	}

	if (paper_number) {
		data.paper_number = paper_number
	}

	if (series) {
		if (
			['JANUARY', 'JUNE', 'NOVEMBER', 'MARCH'].includes(series.toUpperCase())
		) {
			data.series = series.toUpperCase()
		}
	}

	if (public_from) {
		data.public_from = moment(public_from, 'DD/MM/YYYY HH:mm').toISOString()
	}

	if (content_type) {
		const ct = contentTypes.find(
			(item: ContentType) => item.name === content_type
		)
		if (ct) {
			data.content_type = ct.getId()
		}
	}

	if (tag) {
		data.__tag = tag.split(',').map((item) => item.trim())
	}

	if (assessment) {
		if (assessment === 'All') {
			data.all_assessments = true
			data.all_child_assessments = true
		} else {
			const assessmentRefs: (Assessment | QualificationSize)[] = []
			const assessments = assessment.split(',').map((item) => item.trim())
			assessments.forEach((code) => {
				const [found] = productData.getAssessment(code)
				if (found) {
					assessmentRefs.push(found)
					if (found.getType() === Types.ASSESSMENT) {
						data.assessment = Array.from(
							new Set([...Lists.default(data.assessment), found.getId()])
						)
					} else {
						data.qualification_size = Array.from(
							new Set([
								...Lists.default(data.qualification_size),
								found.getId()
							])
						)
					}
				}
			})

			if (unit) {
				if (unit === 'All') {
					data.all_child_assessments = true
					assessmentRefs.forEach((a) => {
						if (a.getType() === Types.ASSESSMENT) {
							;(a as Assessment).childAssessments.forEach((c) => {
								data.child_assessment = Array.from(
									new Set([
										...Lists.default(data.child_assessment),
										c.getId()
									])
								)
							})
						} else {
							;(a as QualificationSize).units.forEach((c) => {
								data.ct_unit = Array.from(
									new Set([...Lists.default(data.ct_unit), c.getId()])
								)
							})
						}
					})
				} else {
					const units = unit.split(',').map((item) => item.trim())
					units.forEach((code) => {
						const [found] = productData.getUnit(code)
						if (found) {
							if (found.getType() === Types.CT_UNIT) {
								data.ct_unit = Array.from(
									new Set([...Lists.default(data.ct_unit), found.getId()])
								)
							} else {
								data.child_assessment = Array.from(
									new Set([
										...Lists.default(data.child_assessment),
										found.getId()
									])
								)
							}
						}
					})
				}
			}

			if (component) {
				if (component === 'All') {
					assessmentRefs.forEach((a) => {
						;(a as Assessment).components.forEach((c) => {
							data.component = Array.from(
								new Set([...Lists.default(data.component), c.getId()])
							)
						})
					})
				} else {
					const units = component.split(',').map((item) => item.trim())
					units.forEach((code) => {
						const [found] = productData.getUnit(code)
						if (found) {
							data.component = Array.from(
								new Set([...Lists.default(data.component), found.getId()])
							)
						}
					})
				}
			}
		}
	}

	return new BulkFile({
		filename,
		data
	})
}
